import React, { type MouseEventHandler, useEffect } from 'react';
import { useInjection } from 'inversify-react';

import { observer } from 'mobx-react';
import { ViewPort } from '@/components/viewPort/ViewPort';

import { TYPES as ImagesTYPES } from './iocTypes';
import { TYPES } from '@/engine/ioc';

import NavigationControl from '@/components/navigationControl/NavigationControl';
import Menu from '@mui/material/Menu';

import styled from 'styled-components';
import MenuItem from '@mui/material/MenuItem';

import { ClippingButton } from '@/components/clipping/ClippingButton';
import { ImageSliderComponent } from '@/engine/app/modules/images/imageSliderComponent/imageSliderComponent.tsx';
import { Box, CircularProgress } from '@mui/material';

import { ImageVisibilityButton } from '@/components/imageVisibility/ImageVisibilityButton';
import { FluoroAnglesLabels } from '@/components/fluoroAngles/FluoroAnglesLabels';
import { ImagesModeSwitcher } from '@/components/imagesModeSwitcher/ImagesModeSwitcher';
import { useViewPortManipulationApi } from '@/components/layout/useViewPortManipulationApi';
import { useContextMenu } from '@/components/layout/views/useContextMenu';
import { useSurgerySpecificUiConfigValue } from '../ui';
import { SynchronizeViews } from '@/components/viewsSynchronization/SynchronizeViews';
import { ResetContrast } from '@/components/resetContrast/ResetContrast';

import { ImagesClippingController } from '@/components/clipping/ImagesClippingController';
import { FluoroAnglesPlacement } from '@/components/fluoroAngles/interfaces.ts';
import { ContextEvents, type ImagesViewPortProps } from './interfaces';

import type { ViewsService } from '../../Views.service';
import type * as THREE from 'three';
import type { ImagesService } from './Images.service';
import { ImageRotationComponent } from '@/engine/app/modules/images/imageRotationComponent/imageRotationComponent.tsx';
import { ResetCameraStrategy } from '@/components/navigationControl/NavigationController';

export const ImagesViewPort: React.FC<ImagesViewPortProps> = observer(
  ({ descriptor, sliceType, bgColor, isMaximized, clippingManager }) => {
    const imagesService = useInjection<ImagesService>(ImagesTYPES.ImagesService);
    const imagesInfo = imagesService.getImageSliceOrientationInfo(sliceType);
    const viewService: ViewsService = useInjection<ViewsService>(TYPES.ViewsService);
    const showFluoroAnglesLabels = useSurgerySpecificUiConfigValue<boolean>('showFluoroAnglesLabels');
    const showImageModes = useSurgerySpecificUiConfigValue<boolean>('hasImageModesFunctionality');

    const [
      viewPortManipulationApi,
      navigationControlIntegrationApi,
      viewPortContextIntegrationApi,
      fluoroAnglesIntegrationApi
    ] = useViewPortManipulationApi();

    const [clippingController, setClippingController] = React.useState<ImagesClippingController | undefined>();

    navigationControlIntegrationApi.setCenterPointProvider(
      (): THREE.Vector3 => imagesService.getSlicesIntersectionPoint()
    );

    const [
      contextMenu,
      contextMode,
      setContextMode,
      handleContextMenu,
      handleContextClose,
      handleRemove,
      handleAdd,
      handleReIndicate
    ] = useContextMenu(viewPortContextIntegrationApi);

    useEffect(() => {
      if (clippingManager && imagesInfo.areImagesReady) {
        const controller = new ImagesClippingController(sliceType, imagesService, viewService, descriptor);
        clippingManager.registerClippingController(descriptor, controller);
        setClippingController(controller);

        return (): void => {
          setClippingController(undefined);
          clippingManager.registerClippingController(descriptor, undefined);
          controller.dispose();
        };
      }
    }, [clippingManager, imagesInfo.areImagesReady, imagesService, descriptor, sliceType, viewService]);

    return (
      <Container onContextMenu={handleContextMenu as unknown as MouseEventHandler<HTMLDivElement>}>
        <ViewPort
          descriptor={descriptor}
          bgColor={bgColor}
          isMaximized={isMaximized}
          viewPortManipulationApi={viewPortManipulationApi}
          contextStateApi={{ setContextState: setContextMode }}
        />
        <Buttons $isMaximized={!!isMaximized}>
          <SynchronizeViews descriptor={descriptor} isMaximized={isMaximized} />
          <ClippingButton descriptor={descriptor} controller={clippingController} isMaximized={isMaximized} />
          <ImageVisibilityButton descriptor={sliceType} isMaximized={isMaximized} />
          <ResetContrast descriptor={sliceType} isMaximized={isMaximized} />
        </Buttons>
        <NavigationControl
          descriptor={descriptor}
          isMaximized={isMaximized}
          integrationApi={navigationControlIntegrationApi}
          resetCameraStrategy={ResetCameraStrategy.CentrifyOnly}
        />
        {showFluoroAnglesLabels && (
          <FluoroAnglesLabels
            $placement={FluoroAnglesPlacement.Image}
            $withShadow
            $isMaximized={isMaximized}
            $integrationApi={fluoroAnglesIntegrationApi}
          />
        )}
        {imagesInfo.areImagesReady && imagesInfo.slicesCount > 1 && (
          <ImageSliderComponent isMaximized={isMaximized} sliceType={sliceType} />
        )}
        {imagesInfo.areImagesReady && imagesInfo.slicesCount === 0 && (
          <ImageRotationComponent isMaximized={isMaximized} sliceType={sliceType} />
        )}
        {imagesInfo.areImagesReady && showImageModes && (
          <ImagesModeSwitcher isMaximized={isMaximized} sliceType={sliceType} />
        )}
        {!imagesInfo.areImagesReady && (
          <LoadingInformationContainer data-testid={'image-loading-' + imagesInfo.areImagesReady}>
            <LoadingContent>
              <div
                style={{
                  gridArea: 'icon',
                  alignItems: 'center',
                  justifyContent: 'center',
                  display: 'flex'
                }}
              >
                {!imagesInfo.areImagesReady && (
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress style={{ color: '#0058a5' }} value={imagesInfo.loadingPercentage} />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {imagesInfo.loadingPercentage}%
                    </Box>
                  </Box>
                )}
              </div>
              <div style={{ gridArea: 'text' }}>Loading images</div>
            </LoadingContent>
          </LoadingInformationContainer>
        )}
        <Menu
          open={contextMenu !== null}
          onClose={handleContextClose}
          anchorReference="anchorPosition"
          anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        >
          {contextMode === ContextEvents.REINDICATE && <MenuItem onClick={handleReIndicate}>Re-indicate</MenuItem>}
          {contextMode === ContextEvents.ADD && <MenuItem onClick={handleAdd}>Add point</MenuItem>}
          {contextMode === ContextEvents.REMOVE && <MenuItem onClick={handleRemove}>Delete point</MenuItem>}
        </Menu>
      </Container>
    );
  }
);

const Container = styled.div`
  width: 100%;
  height: 100%;
`;

const LoadingInformationContainer = styled.div`
  position: absolute;
  bottom: 0;
  padding: 0.5em;
  color: white;
  z-index: 200;
  font-size: 0.7em;
  justify-content: center;
  display: flex;
  align-items: center;
  background-color: #000;
  width: 100%;
  height: 100%;
`;

const LoadingContent = styled.div`
  display: grid;
  grid-template-areas:
    'icon'
    'text';
`;

const Buttons = styled.div<{ $isMaximized: boolean; $topOffset?: number }>`
  display: flex;
  flex-direction: column;
  position: absolute;
  left: ${({ $isMaximized }): string => ($isMaximized ? '1.5rem' : '0')};
  top: ${({ $isMaximized }): string => ($isMaximized ? `1rem` : `2rem`)};
`;
